import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

function waitForNextPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
}

function triggerPrintFromBlob(blobUrl: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    iframe.style.visibility = "hidden";
    document.body.appendChild(iframe);

    let finished = false;
    const cleanup = () => {
      if (finished) return;
      finished = true;
      try {
        document.body.removeChild(iframe);
      } catch {
        /* already removed */
      }
      URL.revokeObjectURL(blobUrl);
      resolve();
    };

    const doPrint = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } catch (err) {
        reject(err);
        return;
      }
      setTimeout(cleanup, 1000);
    };

    iframe.onload = () => setTimeout(doPrint, 300);
    iframe.src = blobUrl;
    setTimeout(() => {
      if (!finished) doPrint();
    }, 1500);
  });
}

export type PrintElementOptions = {
  contentSelector?: string;
  /** PDF page width in mm (default 210 = A4). Use 80 for thermal token printers. */
  paperWidthMm?: number;
};

export async function printElementToPdf(
  element: HTMLElement,
  options?: PrintElementOptions
): Promise<void> {
  const paperWidthMm = options?.paperWidthMm ?? 210;
  const defaultWidthPx = Math.round((paperWidthMm * 96) / 25.4);
  const target =
    (options?.contentSelector
      ? (element.querySelector(options.contentSelector) as HTMLElement | null)
      : null) ?? element;

  if (!target) {
    throw new Error("Print target not found");
  }

  const host = target.parentElement ?? target;
  const previousHostDisplay = host.style.display;
  const previousHostPosition = host.style.position;
  const previousHostLeft = host.style.left;
  const previousHostTop = host.style.top;
  const previousHostWidth = host.style.width;
  const previousHostOpacity = host.style.opacity;
  const previousHostVisibility = host.style.visibility;
  const previousTargetDisplay = target.style.display;

  host.style.display = "block";
  host.style.position = "fixed";
  host.style.left = "0";
  host.style.top = "0";
  host.style.width = `${target.scrollWidth || defaultWidthPx}px`;
  host.style.opacity = "1";
  host.style.visibility = "visible";
  target.style.display = "block";

  await waitForNextPaint();

  try {
    const canvas = await html2canvas(target, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
      logging: false,
    });

    if (!canvas.width || !canvas.height) {
      throw new Error("Print preview is empty");
    }

    const imgData = canvas.toDataURL("image/png");
    const imgHeight = (canvas.height / canvas.width) * paperWidthMm;

    const pdf = new jsPDF({
      orientation: "p",
      unit: "mm",
      format: [paperWidthMm, Math.max(imgHeight, paperWidthMm < 80 ? 20 : 40)],
    });

    pdf.addImage(imgData, "PNG", 0, 0, paperWidthMm, imgHeight, "", "FAST");

    const pdfBlob = pdf.output("blob");
    const blobUrl = URL.createObjectURL(pdfBlob);
    await triggerPrintFromBlob(blobUrl);
  } finally {
    host.style.display = previousHostDisplay;
    host.style.position = previousHostPosition;
    host.style.left = previousHostLeft;
    host.style.top = previousHostTop;
    host.style.width = previousHostWidth;
    host.style.opacity = previousHostOpacity;
    host.style.visibility = previousHostVisibility;
    target.style.display = previousTargetDisplay;
  }
}
